import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Search, Loader2, History, Filter, X, FileText,
  Lightbulb, FolderKanban, Map, Video, BookOpen, GitBranch, Settings, Users
} from 'lucide-react';

const ENTITY_ICONS = {
  idea: Lightbulb, project: FolderKanban, process_map: Map, process_capture: Video,
  sop: BookOpen, process: GitBranch, department: Settings, user: Users,
};

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  submit: 'bg-amber-100 text-amber-700',
  approve: 'bg-emerald-100 text-emerald-700',
  reject: 'bg-red-100 text-red-700',
  retire: 'bg-slate-100 text-slate-600',
  convert: 'bg-purple-100 text-purple-700',
  batch_import: 'bg-teal-100 text-teal-700',
  start_review: 'bg-blue-100 text-blue-700',
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityType) params.append('entity_type', entityType);
      if (action) params.append('action', action);
      if (userId) params.append('user_id', userId);
      if (search) params.append('search', search);
      const r = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(r.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entityType, action, userId, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    api.get('/audit-logs/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const hasFilters = entityType || action || userId || search;

  const clearFilters = () => {
    setEntityType('');
    setAction('');
    setUserId('');
    setSearch('');
  };

  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Audit Log</h1>
          <p className="text-slate-500 text-sm mt-1">
            {stats ? `${stats.total} total events tracked` : 'Activity history across the platform'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search logs..." className="pl-9 h-9" data-testid="audit-search" />
              </div>
            </div>
            <div className="w-40">
              <Select value={entityType || 'all'} onValueChange={v => setEntityType(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9" data-testid="entity-type-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {stats?.entity_types?.map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Select value={action || 'all'} onValueChange={v => setAction(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9" data-testid="action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {stats?.actions?.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={userId || 'all'} onValueChange={v => setUserId(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9" data-testid="user-filter"><SelectValue placeholder="User" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {stats?.users?.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-400 h-9">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Log List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#235894]" /></div>
      ) : logs.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <History className="w-12 h-12 mx-auto mb-4 text-slate-200" />
          <p className="text-slate-500">{hasFilters ? 'No logs match your filters' : 'No audit logs yet'}</p>
        </CardContent></Card>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">When</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const Icon = ENTITY_ICONS[log.entity_type] || FileText;
                return (
                  <tr key={log.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">{log.user_name}</td>
                    <td className="px-4 py-3">
                      <Badge className={ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600'}>
                        {log.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 text-slate-600">
                        <Icon className="w-4 h-4 text-slate-400" />
                        {log.entity_type?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-md truncate">{log.details || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-slate-50 text-xs text-slate-400 border-t">
            Showing {logs.length} events
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
